import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Divider,
  Container,
  ListItemButton,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  MenuBook as MenuBookIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';

interface NavigationItem {
  key: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

const ResponsiveNavigation: React.FC = () => {
  const theme = useTheme();
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const navigationItems: NavigationItem[] = [
    { key: 'dashboard', label: t('organisation.dashboard'), path: '/dashboard', icon: <DashboardIcon /> },
    { key: 'menus', label: t('organisation.menuManagement'), path: '/menus', icon: <MenuBookIcon /> },
    { key: 'shifts', label: t('organisation.shiftManagement'), path: '/shifts', icon: <ScheduleIcon /> },
    { key: 'users', label: t('organisation.userManagement'), path: '/users', icon: <PeopleIcon /> },
  ];

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Don't render navigation items when loading or not authenticated
  if (isLoading) {
    return (
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="h6"
              noWrap
              component={Link}
              to="/"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 700,
                letterSpacing: '.3rem',
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              BCS
            </Typography>
            <CircularProgress color="inherit" size={24} />
          </Toolbar>
        </Container>
      </AppBar>
    );
  }

  const DrawerContent = () => (
    <Box
      sx={{
        width: 250,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      role="navigation"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        }}
      >
        <Typography variant="h6" component="div">
          BCS Organisation Portal
        </Typography>
        {isMobile && (
          <IconButton
            color="inherit"
            onClick={handleDrawerToggle}
            sx={{ display: { md: 'none' } }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <Divider />
      {isAuthenticated ? (
        <>
          <List sx={{ flexGrow: 1 }}>
            {navigationItems.map((item) => (
              <ListItem 
                key={item.key} 
                disablePadding
                sx={{
                  backgroundColor: isActiveRoute(item.path) 
                    ? theme.palette.action.selected 
                    : 'transparent'
                }}
              >
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={() => isMobile && handleDrawerToggle()}
                  selected={isActiveRoute(item.path)}
                >
                  <ListItemIcon
                    sx={{
                      color: isActiveRoute(item.path)
                        ? theme.palette.primary.main
                        : theme.palette.text.primary
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label}
                    primaryTypographyProps={{
                      color: isActiveRoute(item.path)
                        ? theme.palette.primary.main
                        : theme.palette.text.primary
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
          <ListItem disablePadding>
            <ListItemButton onClick={logout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t('logout')} />
            </ListItemButton>
          </ListItem>
        </>
      ) : (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            component={Link}
            to="/login"
            variant="contained"
            color="primary"
            fullWidth
          >
            {t('login')}
          </Button>
        </Box>
      )}
      <Divider />
      <Box sx={{ p: 2 }}>
        <LanguageSwitcher />
      </Box>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} BCS
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar 
            disableGutters
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isAuthenticated && isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Typography
                variant="h6"
                noWrap
                component={Link}
                to={isAuthenticated ? '/dashboard' : '/login'}
                sx={{
                  mr: 2,
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  letterSpacing: '.3rem',
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                BCS
              </Typography>
            </Box>

            {/* Only show navigation items when authenticated */}
            {isAuthenticated ? (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {!isMobile && navigationItems.map((item) => (
                  <Button
                    key={item.key}
                    component={Link}
                    to={item.path}
                    color="inherit"
                    startIcon={item.icon}
                    sx={{
                      position: 'relative',
                      '&::after': isActiveRoute(item.path) ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '3px',
                        backgroundColor: 'currentColor',
                        borderRadius: '2px 2px 0 0',
                      } : undefined,
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                
                <LanguageSwitcher />
                
                <Tooltip title={t('profile')}>
                  <IconButton
                    onClick={handleUserMenuOpen}
                    color="inherit"
                    sx={{ ml: 2 }}
                  >
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32,
                        bgcolor: theme.palette.secondary.main
                      }}
                    >
                      {user?.name?.charAt(0) || <AccountCircleIcon />}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                
                <Menu
                  anchorEl={userMenuAnchorEl}
                  open={Boolean(userMenuAnchorEl)}
                  onClose={handleUserMenuClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="subtitle1">{user?.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
                  </Box>
                  <Divider />
                  <MenuItem component={Link} to="/profile" onClick={handleUserMenuClose}>
                    <ListItemIcon>
                      <AccountCircleIcon fontSize="small" />
                    </ListItemIcon>
                    {t('organisation.profile.root')}
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    {t('logout')}
                  </MenuItem>
                </Menu>
              </Box>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleLogin}
              >
                {t('login')}
              </Button>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Only render drawer when authenticated */}
      {isAuthenticated && (
        <Box 
          component="nav" 
          sx={{ 
            width: { md: 250 }, 
            flexShrink: { md: 0 } 
          }}
        >
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            anchor="left"
            open={isMobile && drawerOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better mobile performance
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: 250 
              },
            }}
          >
            <DrawerContent />
          </Drawer>
        </Box>
      )}

      {/* Toolbar spacer */}
      <Toolbar />
    </>
  );
};

export default ResponsiveNavigation;